import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Table, Space, Popconfirm, Button, message, Flex, Input, Form, Modal, Row, Col, Typography } from "antd";
import { HomeOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "../../axios-interceptor.js";
import "../../style/organisationConfig.css"
import TableComponent from "../space/components/table/Index";
import BreadCrumb from "../BreadCrumb";
import { getUserSpaceEntitlements } from "../../utils/currentUserDetail";
import { showLoader } from "../../utils/showloader.js";
import { trimStringValues } from "../../utils/trimStrings";
const { TextArea } = Input;
const { Title } = Typography

const BACKEND_API = process.env.REACT_APP_BACKEND_API, tenantId = localStorage.getItem("tenantId");

const RolesAndEntitlements = () => {
    const { t } = useTranslation();
    
    const [selectedRole, setSelectedRole] = useState(null);
    let defaultBreadCrumbItems = [{title: <HomeOutlined />, path: "/app/dashboard"}, { title: t('Roles & Entitlements')}]
    const [rolesData, setRolesData] = useState([])
    const [tempData, setTempData] = useState([])
    const [showForm, setShowForm] = useState(false)
    const [entitlements, setEntitlements] = useState([])
    const [defaultSelectedKeys, setDefaultSelectedKeys] = useState([]);
    const [isSearch, setIsSearch] = useState('')
    const [filteredEntitlements, setFilteredEntitlements] = useState([])
    const [isCopy, setIsCopy] = useState(false)
    const [showRoleEntitlements, setShowRoleEntitlements] = useState(false)
    const [showPermissionModal, setShowPermissionModal] = useState(false)
    const [selectedEntitlements, setSelectedEntitlements] = useState([])
    const [formData, setFormData] = useState(null)
    const { userInfo } = useSelector(state => state.user);
    const adminEntitlements = getUserSpaceEntitlements(null, userInfo.userAccess);

    const fetchEntitlements = useCallback(async () => {
        axios({
            method: "GET",
            url: `${BACKEND_API}/entitlements`,
            headers: {
                userId: 1,
            },
        })
            .then((response) => {
                let data = response.data
                if (data.length > 0) {
                    data = data.map((entitlement, index) => {
                        return { ...entitlement, key: index }
                    })
                    setEntitlements(data)
                    setFilteredEntitlements(data)
                }
            })
            .catch((error) => {
            });

    }, [])

    const fetchRoles = useCallback(async () => {
        showLoader(true)
        await axios({
            method: "GET",
            url: `${BACKEND_API}/role/${tenantId}`,
            headers: {
                userId: 1,
            },
        })
            .then((response) => {
                let data = response.data
                setTempData(data);
                setRolesData(data)
            })
            .catch((error) => {
            });
            showLoader(false)
    }, [])

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        fetchEntitlements();
    }, [fetchEntitlements])

    useEffect(() => {
        if (isSearch) {
            let filteredData = entitlements.filter(entitlement => entitlement.name.includes(isSearch.toLowerCase()))
            setFilteredEntitlements(filteredData)
        } else {
            setFilteredEntitlements(entitlements)
        }
    }, [isSearch])

    const handleDeleteElement = async (role) => {
        showLoader(true)
        await axios({
            method: "DELETE",
            url: `${BACKEND_API}/role/${tenantId}?roleId=${role.roleId}`,
            headers: {
                userId: 1,
            },
        })
            .then((response) => {
                message.success(t('Role deleted successfully'))
                fetchRoles()
            })
            .catch((error) => {
                message.error(t('Failed to delete role. Please try again'))
            });
        showLoader(false)
    }

    const handleEditElement = (role) => {
        let currentEntitlesments = role.entitlements.map(entitlement => {
            let roleIndex = filteredEntitlements.findIndex(entitle => entitlement.name === entitle.name)
            return roleIndex
        })
        let entitlementTableData = filteredEntitlements.map(data => {
            let dataObj = {...data}
            let currentEntitleInRole = role.entitlements.find(entitle => entitle === dataObj.name);
            if(currentEntitleInRole) {
                dataObj.chosen = true;
            } else {
                dataObj.chosen = false
            }
            return dataObj
        })
        setSelectedEntitlements(role.entitlements || [])
        setDefaultSelectedKeys(currentEntitlesments)
        setFilteredEntitlements(entitlementTableData)
        setSelectedRole({ ...role })
        setFormData({ ...role })
        setShowForm(true)
    }

    const onSaveAndEditCancel = (type = 'cancel', record) => {
        if (type === 'cancel') {
            setTempData([...rolesData])
            setIsCopy(false)
        } else {
            onSaveElement(record)
        }
        setSelectedRole(null)
        setSelectedEntitlements([])
        setShowForm(false)
    }

    const onCheck = async (record) => {
        onSaveElement(record)
    };

    const onSaveElement = async(record) => {
        let postData = { entitlements: selectedEntitlements, role: record.role, description: record.description }
        if (record.roleId) {
            postData.roleId = record.roleId
        }
        showLoader(true)
        // return
        await axios({
            method: postData.roleId ? "PUT" : "POST",
            url: `${BACKEND_API}/role`,
            data: { ...postData, tenantId: localStorage.getItem("tenantId") },
            headers: {
                Accept: "application/json",
                userId: 1,
            },
        })
            .then((response) => {
                if (response.data.roleId) {
                    message.success(
                        selectedRole
                            ? t("Role updated successfully")
                            : t("Role created successfully")
                    );
                    fetchRoles();
                    setShowForm(false)
                    setIsCopy(false)
                    setSelectedEntitlements([])
                    setSelectedRole(null)
                } else {
                    message.error(response.data)
                }
            })
            .catch((error) => {
                // TODO: move it to success
            });
        showLoader(false)
    }

    const handleCopyRole = (record) => {
        setSelectedRole(record)
        setIsCopy(true)
        setShowForm(true)
        setSelectedEntitlements(record.entitlements || [])
    }

    const handleEntitlementClick = (entitlements) => {
        setSelectedEntitlements(entitlements)
        setTimeout(() => {
            setShowRoleEntitlements(true)
        }, [200])
    }

    const tableColumns = [
        {
            title: t("Role Title"),
            dataIndex: "role",
            key: "role",
            width: "20%"
        },
        {
            title: t("Assign"),
            dataIndex: "entitlements",
            key: "entitlements",
            width: "20%",
            render: (text, record, rowIndex) => {
                return (
                    <span className="cursor-pointer" style={{ color: "#0958d9" }} onClick={() => handleEntitlementClick(record.entitlements)}>{text.length}{t("Permissions")}</span>
                )
            }
        },
        {
            title: t("Description"),
            dataIndex: "description",
            key: "Description",
            width: "40%",
        }        
    ];
    if(adminEntitlements.includes("Roles_Entitlements_Update")){
        tableColumns.push(
            {
                title: t("Action"),
                dataIndex: "",
                key: "",
                width: "15%",
                render: (text, record, rowIndex) => {
                    return (
                        <Space>
                            {
                                record.isDefault ?
                                    <Popconfirm
                                        title={t("This role cannot be edited")}
                                        description={"Would you like to copy this role?"}
                                        okText={"Yes"}
                                        cancelText={"No"}
                                        onConfirm={() => handleCopyRole(record, rowIndex)}
                                    >
                                        <Button size="small" icon={<EditOutlined />} />
                                    </Popconfirm>
                                    :
                                    <>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => handleEditElement(record, rowIndex)}
                                        />
                                        <Popconfirm
                                            title={t("Delete the role")}
                                            description={t("Are you sure to delete this role?")}
                                            okText={t("Yes")}
                                            cancelText={t("No")}
                                            onConfirm={() => handleDeleteElement(record, rowIndex)}
                                        >
                                            <Button size="small" icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </>
                            }
                        </Space>
                    )
                },
            },
        )
    }

    const handlePermissionModal = () => {
        setShowPermissionModal(true)
    }
    const handlePermissionModalClose = () => {
        setShowPermissionModal(false)
    }

    const tableConfig = {
        type: "table",
        id: "entitlements_table",
        selectable: true,
        columns: [
            {
                title: t('Permission'),
                custom_search: true,
                dataIndex: 'name',
            },
            {
                title: t('Description'),
                dataIndex: 'description',
                no_filter: true
            },
        ]
    }

    const getSelectedData = (selectedData) => {
        localStorage.setItem("selectedRows", JSON.stringify(selectedData))
    }

    const handleEntitlementsSelection = () => {
        handlePermissionModalClose()
        let currentEntitlements = localStorage.getItem("selectedRows") ? JSON.parse(localStorage.getItem("selectedRows")) : []
        if(currentEntitlements.length  > 0) {
            currentEntitlements = currentEntitlements.map(entitle => entitle.name)
        }
        setSelectedEntitlements(currentEntitlements)
        localStorage.removeItem("selectedRows")
    }

    return (
        <div className="page-wrapper admin-config">
            <BreadCrumb items={defaultBreadCrumbItems} marginb="24px" />

            <Row gutter={8}
                style={{
                    paddingBottom: "20px",
                    margin: "0",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.1)"
                }}>
                <Col span={12}>
                    <Title level={3} style={{ margin: 0 }}>
                        {t("Roles and Entitlements")}
                    </Title>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                    {
                        adminEntitlements.includes("Roles_Entitlements_Update") ? 
                            <Space>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowForm(true)}>
                                {t("Add Custom Role")}
                                </Button>
                            </Space>
                        : null
                    }
                </Col>
            </Row>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "normal"
                }}
            >
                <div className="organisation-structure" style={{ width: "70%", paddingRight: "10px" }}>
                    {/* <Form form={form}> */}
                    <Table
                        style={{ width: "100%", paddingTop: "32px" }}
                        dataSource={tempData}
                        columns={tableColumns}
                        pagination={false}
                        size="small" // Set the size to 'small' to reduce the height of rows
                    />
                </div>
                {
                    showForm ?
                        <AddEditRole role={selectedRole} onCheck={onCheck} onSaveAndEditCancel={onSaveAndEditCancel} handlePermissionModal={handlePermissionModal} selectedEntitlements={selectedEntitlements} isCopy={isCopy} roles={rolesData} />
                        : null
                }
            </div>

            <Modal title={t("Assigned Entitlements")} className="columns-selector-modal" footer={false} open={showRoleEntitlements} onOk={() => setShowRoleEntitlements(false)} onCancel={() => setShowRoleEntitlements(false)}>
                <div style={{ maxWidth: "430px", overflow: "hidden" }}></div>
                <Space direction="vertical" className="columns-selector-container" style={{ maxHeight: "400px", overflow: "overlay", paddingBottom: "16px" }}>
                    {
                        selectedEntitlements.map((data, index) => {
                            return <div className="checkbox-row" key={"selected-entitlement-" + index} style={{ justifyContent: "flex-start" }}>
                                <span className="step-circle">{index + 1}</span>
                                <div className="" style={{ marginLeft: "10px" }}>{data}</div>
                            </div>
                        })
                    }
                </Space>
            </Modal>

            <Modal title={t("Assign Entitlements")} className="entitlements-selector-modal" footer={false} open={showPermissionModal} onOk={handlePermissionModalClose} onCancel={handlePermissionModalClose}>
                <div>
                    <Space direction="vertical" className="columns-selector-container">
                        <TableComponent config={{...tableConfig}} data={filteredEntitlements} getSelectedData={getSelectedData} selectedKeys={defaultSelectedKeys} />
                            
                    </Space>
                    <Flex justify="flex-end" gap={8} align="center" style={{ padding: "8px 24px 24px" }}>
                        <Button default style={{ border: "none" }} onClick={() => setShowPermissionModal(false)}>{t("Cancel")}</Button>
                        <Button type="primary" onClick={handleEntitlementsSelection}>{t("Add")}</Button>
                    </Flex>
                </div>
            </Modal>
        </div>
    )
}
export default RolesAndEntitlements

const AddEditRole = ({ role, onCheck, onSaveAndEditCancel, handlePermissionModal, selectedEntitlements, isCopy, roles }) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();

    useEffect(() => {
        let selRole = { ...role }
        if (isCopy) {
            delete selRole.categoryId
            selRole.role = role.role + " Copy"
        }
        form.setFieldsValue(selRole);
        if (role && Object.keys(role).length == 0) {
            form.resetFields()
        }
    }, [form, role]);

    const handleRoleSave = async () => {
        try {
            const values = trimStringValues(await form.validateFields());
            onCheck({ ...role, role: values.role, description: values.description });
        } catch (errorInfo) {
        }
    };


    return (
        <div style={{ width: "30%", padding: "32px 12px 0 12px", borderLeft: "1px solid rgba(0, 0, 0, 0.1)", textAlign: "left", position: "relative" }}>
            <Row gutter={8}>
                <Col span={24}>
                    <Typography.Title level={4} style={{ marginTop: "0px" }}>
                        {t("Component Details")}
                    </Typography.Title>
                </Col>

            </Row>
            <Form
                layout="vertical"
                form={form}
                name="role-form"
                onFinish={handleRoleSave}
            >
                <Form.Item name="role" rules={[
                    { required: true, whitespace: true, message: t('Role title is required') },
                    { 
                        validator: async (_, value) => {
                            if(!value) return;
                            let valExists = roles.find(data => data.role === value && (!role || isCopy || data.roleId !== role.roleId));
                            if(!valExists) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error(t('Role must be unique!')))
                        }
                    }
                    
                ]} label={t("Role Title")} style={{ marginBottom: "15px" }}>
                    <Input placeholder={t("Enter role title")} />
                </Form.Item>
                <Form.Item name="description" label={t("Description")} style={{ marginBottom: "15px" }}>
                    <TextArea placeholder={t("Description")} rows={2} />
                </Form.Item>
                <div>
                    <Button style={{ borderColor: "#1677FF", color: "#1677FF", marginBottom: "40px" }} icon={<PlusOutlined />} onClick={handlePermissionModal}>{t("Add Entitlement")}</Button>
                    <span style={{ marginLeft: "5px" }}>{selectedEntitlements.length} {t("Entitlements selected")}</span>

                </div>
                <Space>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">{t("Save")}</Button>
                    </Form.Item>
                    <Form.Item>
                        <Button onClick={() => onSaveAndEditCancel('cancel', role)}>{t("Cancel")}</Button>
                    </Form.Item>
                </Space>
            </Form>
        </div>
    )
}